import { useState, useEffect, useRef } from 'react';
import { IntlShape } from 'react-intl';
import { Icon } from '../icons/Icon';
import { usePageContext } from 'vike-react/usePageContext';
import { Autocomplete, AutocompleteHandle } from '../Autocomplete';
import { Link } from '../Link';
import { useSearchFilters } from '../../contexts/SearchContext';
import { trackEvent } from '../../utils/analytics/analytics';

// iOS 18 style font stack
const iosFontStack = "font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]";

interface MobileBottomNavProps {
  intl: IntlShape;
  isSearchPage: boolean;
  isAllFiltersModalOpen: boolean;
  setIsAllFiltersModalOpen: (open: boolean) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  mobileAutocompleteRef: React.RefObject<AutocompleteHandle | null>;
  closeAllAutocompletes: () => void;
  handleAutocompleteOpen: () => void;
}

export const MobileBottomNav = ({
  intl,
  isSearchPage,
  isAllFiltersModalOpen,
  setIsAllFiltersModalOpen,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  mobileAutocompleteRef,
  closeAllAutocompletes,
  handleAutocompleteOpen,
}: MobileBottomNavProps) => {
  const pageContext = usePageContext();
  const locale = (pageContext?.locale || 'en') as 'en' | 'fi' | 'sv';
  const searchPageUrl = `/${locale}`;
  const pathname = (pageContext as any)?.urlPathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const isJobDetailPage = /\/job\/[^/]+\/[^/]+/.test(pathname);
  
  // Don't render nav for job detail pages - they have their own
  if (isJobDetailPage) {
    return null;
  }
  
  const { filters } = useSearchFilters();
  const filtersObj = (typeof filters === 'object' && filters) ? filters as Record<string, string[]> : {};
  
  // Determine which filter categories are active
  const hasLocationFilters = !!(filtersObj.location?.length || filtersObj.remoteness?.length);
  const hasSalaryFilters = !!(filtersObj.salaryRangeMin?.length || filtersObj.vacationWeeks?.length);
  const hasCompanyFilters = !!(filtersObj.companyId?.length || filtersObj.companyIndustry?.length);
  const hasJobFilters = !!(
    filtersObj.jobType?.length || 
    filtersObj.normalisedRoleLevel?.length || 
    filtersObj.requiredLanguages?.length || 
    filtersObj.benefits?.length || 
    filtersObj.skillsAndTechnologies?.length ||
    filtersObj.field?.length ||
    filtersObj.educationLevel?.length ||
    filtersObj.peopleManagementTier?.length ||
    filtersObj.lastSeenMin?.length ||
    filtersObj.excludeSaved?.length ||
    filtersObj.excludeViewed?.length ||
    filtersObj.excludeApplied?.length
  );
  
  const activeCategories = [
    hasLocationFilters && 'location',
    hasSalaryFilters && 'salary',
    hasCompanyFilters && 'company',
    hasJobFilters && 'job',
  ].filter(Boolean) as string[];
  
  const hasAnyFilters = activeCategories.length > 0;
  
  // Create gradient stops for multi-color fill
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  useEffect(() => {
    // Check dark mode on client side
    const checkDarkMode = () => {
      if (typeof window !== 'undefined') {
        setIsDarkMode(document.documentElement.classList.contains('dark'));
      }
    };
    checkDarkMode();
    
    // Watch for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    if (typeof window !== 'undefined') {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    
    return () => observer.disconnect();
  }, []);
  
  const getGradientStops = () => {
    if (!hasAnyFilters) return null;
    
    const colors = {
      location: { light: '#3B5E45', dark: '#98CFA8' },
      salary: { light: '#6E4F00', dark: '#D4A820' },
      company: { light: '#5A3E20', dark: '#D4B898' },
      job: { light: '#5A4A3A', dark: '#C4A882' },
    };
    
    const colorKey = isDarkMode ? 'dark' : 'light';
    
    const stops: Array<{ offset: string; color: string }> = [];
    const segmentSize = 100 / activeCategories.length;
    
    activeCategories.forEach((category, index) => {
      const color = colors[category as keyof typeof colors]?.[colorKey] || '#6b7280';
      stops.push({
        offset: `${index * segmentSize}%`,
        color,
      });
      if (index < activeCategories.length - 1) {
        stops.push({
          offset: `${(index + 1) * segmentSize}%`,
          color,
        });
      }
    });
    
    return stops;
  };
  
  const gradientStops = getGradientStops();
  
  // Custom funnel icon with gradient fill
  const gradientId = `filterGradient-${activeCategories.join('-')}-${isDarkMode ? 'dark' : 'light'}`;
  
  const FilterIcon = () => {
    if (!hasAnyFilters) {
      return (
        <span className="text-[#7A6E5F] dark:text-[#B8A48C] block leading-none">
          <Icon name="funnel" useMaskForColor className="w-6 h-6" />
        </span>
      );
    }
    
    return (
      <span className="block leading-none">
        <svg
          className="w-6 h-6 block"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={0}
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              {gradientStops?.map((stop, idx) => (
                <stop key={idx} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          </defs>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
            fill={`url(#${gradientId})`}
          />
        </svg>
      </span>
    );
  };

  return (
    <nav
      className={`fixed bottom-0 left-0 w-full z-[60] flex items-center gap-2 px-3 py-2 border-t border-[#DDD5C8] dark:border-[#4A3520] lg:hidden bg-white/95 dark:bg-[#1F1509]/95 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] dark:shadow-none rounded-t-2xl transition-colors duration-300 ${iosFontStack} max-w-full overflow-hidden`}
      style={{
        paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
        pointerEvents: 'auto',
      }}
    >
      {/* Regular pages: Show search and filters */}
      <>
        {/* Search bar */}
        <div className="flex-1 min-w-0">
          <Autocomplete
            ref={mobileAutocompleteRef}
            onOpen={handleAutocompleteOpen}
            popUp={true}
            placeholder={intl.formatMessage({
              id: 'instantsearch.searchBoxLink.placeholder',
              description: 'Placeholder for the search box',
              defaultMessage: 'Search for jobs',
            })}
          />
        </div>
        {/* All Filters button - navigate to search page if not on search page */}
        {isSearchPage ? (
          <button
            className="flex items-center justify-center outline-none flex-none rounded-full p-3 font-bold border border-[#DDD5C8] dark:border-[#4A3520] hover:border-[#3B5E45]/50 dark:hover:border-[#98CFA8]/40 hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-all duration-200 cursor-pointer bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none"
            onClick={() => {
              // Close autocomplete when All Filters button is clicked
              closeAllAutocompletes();

              const willOpen = !isAllFiltersModalOpen;
              if (willOpen) {
                trackEvent({ name: 'nav_menu_open', params: { menu: 'mobile_bottom', locale } });
              }
              setIsAllFiltersModalOpen(willOpen);
              // Close mobile menu if opening All Filters
              if (willOpen && isMobileMenuOpen) {
                setIsMobileMenuOpen?.(false);
              }
            }}
            aria-label={intl.formatMessage({
              id: 'search.refinements.allFilters.aria',
              defaultMessage: 'All Filters',
              description: 'Accessibility label for all filters button',
            })}
          >
            <FilterIcon />
          </button>
        ) : (
          <Link
            href={searchPageUrl}
            className="flex items-center justify-center outline-none flex-none rounded-full p-3 font-bold border border-[#DDD5C8] dark:border-[#4A3520] hover:border-[#3B5E45]/50 dark:hover:border-[#98CFA8]/40 hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-all duration-200 cursor-pointer bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none"
            aria-label={intl.formatMessage({
              id: 'search.refinements.allFilters.aria',
              defaultMessage: 'All Filters',
              description: 'Accessibility label for all filters button'
            })}
          >
            <FilterIcon />
          </Link>
        )}
        {/* Hamburger menu button */}
        <button
          className="flex items-center justify-center outline-none flex-none rounded-full p-3 font-bold border border-[#DDD5C8] dark:border-[#4A3520] hover:border-[#3B5E45]/40 dark:hover:border-[#98CFA8]/35 hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-all duration-200 cursor-pointer bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none"
          onClick={() => {
            const willOpen = !(isMobileMenuOpen ?? false);
            if (willOpen) {
              trackEvent({ name: 'nav_menu_open', params: { menu: 'mobile_bottom', locale } });
            }
            setIsMobileMenuOpen?.(willOpen);
            // Close All Filters modal if opening mobile menu
            if (willOpen && isAllFiltersModalOpen) {
              setIsAllFiltersModalOpen(false);
            }
          }}
          aria-label={intl.formatMessage({
            id: 'navigation.menu.open',
            defaultMessage: 'Open menu',
            description: 'Button aria-label to open mobile navigation menu',
          })}
        >
          <span className="text-[#3D3529] dark:text-[#C4A882] block leading-none" aria-hidden>
            <Icon name="bars3" useMaskForColor className="w-6 h-6" />
          </span>
        </button>
      </>
    </nav>
  );
};
